/**
 * Filter Configuration
 * 
 * Defines which filters are available for each post type and their relationships
 */

import isEmpty from 'lodash/isEmpty';

export const POST_TYPE_CONFIG = {
  // Top Level Filters
  'books': {
    browseByAtTopLevel: true,
    associations: [ 'age_range', 'brand', 'book_category', 'collection', 'imprint', 'series', 'book_theme' ],
    availableFilters: [ 'childrens-categories', 'featured', 'format', 'language' ],
    showFilters: true
  },
  'collection': {
    browseByAtTopLevel: true,
    associations: [],
    availableFilters: [],
    showFilters: false
  },
  // Sub Filters
  'age-range': {
    browseByAtTopLevel: false,
    associations: [ 'brand', 'book_category', 'imprint', 'series', 'book_theme' ],
    availableFilters: [ 'brand', 'category', 'childrens-categories', 'featured', 'format', 'language', 'imprint', 'series', 'theme' ]
  },
  'brand': {
    browseByAtTopLevel: false,
    associations: [ 'age_range', 'book_category', 'imprint', 'series', 'book_theme' ],
    availableFilters: [ 'age-range', 'category', 'childrens-categories', 'featured', 'format', 'imprint', 'language', 'series', 'theme' ]
  },
  'book-category': {
    browseByAtTopLevel: false,
    associations: [ 'age_range','brand', 'imprint', 'series', 'book_theme' ],
    availableFilters: [ 'age-range', 'brand', 'childrens-categories', 'featured', 'format', 'imprint', 'language', 'series', 'theme' ]
  },
  'imprint': {
    browseByAtTopLevel: false,
    associations: [ 'age_range', 'brand', 'book_category', 'series', 'book_theme' ],
    availableFilters: [ 'age-range', 'brand', 'childrens-categories', 'category', 'featured', 'format', 'language', 'series', 'theme' ]
  },
  'book-theme': {
    browseByAtTopLevel: false,
    associations: [ 'age_range', 'brand', 'book_category', 'imprint', 'series' ],
    availableFilters: [ 'age-range', 'brand', 'childrens-categories', 'category', 'featured', 'format', 'language', 'series', 'theme' ]
  },
  'series': {
    browseByAtTopLevel: false,
    associations: [],
    availableFilters: [],
    showFilters: false
  }
};

export const FILTER_COMPONENTS = {
  'age-range': {
    component: 'FilterPostItem',
    props: {
      filterPostType: 'agerange',
      dataCode: 'data-age-range-code',
      title: 'Age Range'
    }
  },
  'brand': {
    component: 'BrandFilter',
    props: {
      filterPostType: 'brand',
      dataCode: 'data-brand-code',
      title: 'Brand'
    }
  },
  'category': {
    component: 'FilterPostItem',
    props: {
      filterPostType: 'bookcategory',
      dataCode: 'data-category-code',
      title: 'Category'
    }
  },
  'imprint': {
    component: 'FilterPostItem',
    props: {
      filterPostType: 'imprint',
      dataCode: 'data-imprint-code',
      title: 'Imprint'
    }
  },
  'series': {
    component: 'FilterPostItem',
    props: {
      filterPostType: 'series',
      dataCode: 'data-series-code',
      title: 'Series'
    }
  },
  'theme': {
    component: 'ThemeFilter',
    props: {
      filterPostType: 'theme',
      dataCode: 'data-theme-code',
      title: 'Theme'
    }
  },
  'featured': {
    component: 'FeaturedFilter',
    props: {}
  },
  'format': {
    component: 'FormatFilter',
    props: {
      dataCode: 'data-format-codes'
    }
  },
  'language': {
    component: 'LanguageFilter',
    props: {
      dataCode: 'data-language-codes'
    }
  },
  'childrens-categories': {
    component: 'ChildrensCategoriesFilter',
    props: {
      dataCode: 'data-childrens-categories'
    }
  },
  'collection': {
    component: 'FilterPostItem',
    props: {
      filterPostType: 'collection',
      dataCode: 'data-collection-code',
      title: 'Collections'
    }
  },
  'author-last-initial': {
    component: 'AuthorLastInitialFilter',
    props: {
      dataCode: 'data-author-last-initial'
    }
  }
};

export const DATA_MAPPING = {
  'age-range': 0,
  'category': 1,
  'brand': 2,
  'imprint': 3,
  'series': 4,
  'theme': 5,
  'collection': 6
};

/**
 * ACF Field Mappings
 * Maps filterPostType to their corresponding ACF fields and context codes
 */
export const ACF_FIELD_MAPPING = {
  'bookcategory': {
    contextCodeField: 'categoryCode',
    acfField: 'bisac',
    requiresSorting: false
  },
  'imprint': {
    contextCodeField: 'imprintCode',
    acfField: 'imprint_code',
    requiresSorting: true
  },
  'agerange': {
    contextCodeField: 'ageRangeCode',
    acfField: 'age_range_code',
    requiresSorting: false
  },
  'brand': {
    contextCodeField: 'brandCode',
    acfField: 'brand_codes',
    requiresSorting: false
  },
  'booktheme': {
    contextCodeField: 'themeCode',
    acfField: 'theme_codes',
    requiresSorting: false
  },
  'series': {
    contextCodeField: 'seriesCode',
    acfField: 'series_code',
    requiresSorting: false
  },
  'collection': {
    contextCodeField: 'collectionISBNs',
    acfField: 'collection_isbns',
    requiresSorting: false
  }
};

/**
 * Helper functions for FilterPostItem
 */
export const FilterHelpers = {
  /**
   * Get the context code for a given filter post type
   * @param {string} filterPostType - The filter post type
   * @param {Object} context - The context object
   * @returns {string} The context code
   */
  getContextCode: ( filterPostType, context ) => {
    const mapping = ACF_FIELD_MAPPING[filterPostType];
    if ( !mapping ) {
      return context.imprintCode; // fallback
    }
    return context[mapping.contextCodeField];
  },

  /**
   * Get the ACF field value for checkbox
   * @param {string} filterPostType - The filter post type
   * @param {Object} item - The item object
   * @param {Function} sortCode - The sort function for codes that need sorting
   * @returns {string} The ACF field value
   */
  getACFValue: ( filterPostType, item, sortCode ) => {
    const mapping = ACF_FIELD_MAPPING[filterPostType];
    if ( !mapping ) {
      return sortCode( item.ACF?.imprint_code ); // fallback
    }
    
    const value = item.ACF?.[mapping.acfField];
    return mapping.requiresSorting ? sortCode( value ) : value;
  },

  /**
   * Get the checked state for an item
   * @param {string} filterPostType - The filter post type
   * @param {Object} item - The item object
   * @param {Object} checked - The checked state object
   * @param {Function} sortCode - The sort function for codes that need sorting
   * @returns {boolean} The checked state
   */
  getCheckedState: ( filterPostType, item, checked, sortCode ) => {
    if ( isEmpty( checked ) ) return '';
    
    const mapping = ACF_FIELD_MAPPING[filterPostType];
    if ( !mapping ) {
      return checked[sortCode( item.ACF?.imprint_code )]; // fallback
    }
    
    const value = item.ACF?.[mapping.acfField];
    const key = mapping.requiresSorting ? sortCode( value ) : value;
    return checked[key];
  }
};
